/**
 * @file User / material analysis UI — pages/Dashboard/User/MaterialAnalysisInput/components/MaterialSelectionCard.tsx.
 */

import React from "react";
import { Material, SubMaterial } from "../hooks/useMaterialAnalysisData";
import {
  AGNOSTIC_SUB_MATERIAL_LABEL,
  AGNOSTIC_SUB_MATERIAL_VALUE,
  isAgnosticSubMaterialName,
} from "../materialAnalysisConstants";
import {
  MaterialPropertyConfig,
  MATERIAL_ANALYSIS_PROPERTIES,
  NO_MATERIAL_PROPERTY_VALUE,
} from "../materialAnalysisPropertyConfig";

interface MaterialSelectionCardProps {
  materials: Material[];
  subMaterials: SubMaterial[];
  materialId: number | null;
  subMaterialId: number | null;
  subMaterialIsAgnostic: boolean;
  materialPropertyKey: string;
  materialPropertyConfig: MaterialPropertyConfig | null;
  materialPropertyMin: string;
  materialPropertyMax: string;
  /** True while analysis data for the selected material is loading. */
  subMaterialsLoading?: boolean;
  onMaterialChange: (value: string) => void;
  onSubMaterialChange: (value: string) => void;
  onMaterialPropertyKeyChange: (value: string) => void;
  onMaterialPropertyMinChange: (value: string) => void;
  onMaterialPropertyMaxChange: (value: string) => void;
}

/** Exported const — Material Selection Card. */
export const MaterialSelectionCard: React.FC<MaterialSelectionCardProps> = ({
  materials,
  subMaterials,
  materialId,
  subMaterialId,
  subMaterialIsAgnostic,
  materialPropertyKey,
  materialPropertyConfig,
  materialPropertyMin,
  materialPropertyMax,
  subMaterialsLoading = false,
  onMaterialChange,
  onSubMaterialChange,
  onMaterialPropertyKeyChange,
  onMaterialPropertyMinChange,
  onMaterialPropertyMaxChange,
}) => {
  const subTypeDisabled = !materialId || subMaterialsLoading;
  const subTypeOptions = subMaterials.filter(
    (subMaterial) => !isAgnosticSubMaterialName(subMaterial.name)
  );
  const subTypeValue = subMaterialIsAgnostic
    ? AGNOSTIC_SUB_MATERIAL_VALUE
    : subMaterialId ?? "";
  const propertyDisabled = !materialId || !materialPropertyConfig;

  return (
    <section className="analysis-card analysis-form-card">
      <header className="analysis-card__header">
        <h2>Select Material</h2>
        <p>
          Choose your material of interest.{" "}
          Select a broad category (e.g., cement, steel, timber), then refine by type (e.g., OPC Grade 53).{" "}
          You may also specify an allowable property range to narrow the scope.
        </p>
      </header>

      <div className="three-field-row">
        <div className="three-field-cell">
          <label>
            Material <span className="required">*</span>
          </label>
          <select
            value={materialId ?? ""}
            onChange={(event) => onMaterialChange(event.target.value)}
          >
            <option value="">Select Material</option>
            {materials.map((material) => (
              <option key={material.id} value={material.id}>
                {material.name}
              </option>
            ))}
          </select>
          <small className="helper-text">(Select the desired material)</small>
        </div>

        <div className="three-field-cell">
          <label>Material Sub-type</label>
          <select
            value={subTypeValue}
            onChange={(event) => onSubMaterialChange(event.target.value)}
            disabled={subTypeDisabled}
          >
            <option value={AGNOSTIC_SUB_MATERIAL_VALUE}>
              {subMaterialsLoading ? "Loading sub-types…" : AGNOSTIC_SUB_MATERIAL_LABEL}
            </option>
            {subTypeOptions.map((subMaterial) => (
              <option key={subMaterial.id} value={subMaterial.id}>
                {subMaterial.name}
              </option>
            ))}
          </select>
        </div>

        <div className="three-field-cell">
          <label>Material Properties</label>
          <div className="material-property-range">
            <select
              value={materialPropertyKey}
              onChange={(event) => onMaterialPropertyKeyChange(event.target.value)}
              disabled={!materialId}
              aria-label="Material property"
            >
              <option value={NO_MATERIAL_PROPERTY_VALUE}>None</option>
              {MATERIAL_ANALYSIS_PROPERTIES.map((property) => (
                <option key={property.propertyKey} value={property.propertyKey}>
                  {property.label}
                </option>
              ))}
            </select>
            {materialPropertyConfig ? (
              <div className="material-property-range__inputs">
                <input
                  type="number"
                  inputMode="decimal"
                  value={materialPropertyMin}
                  onChange={(event) => onMaterialPropertyMinChange(event.target.value)}
                  placeholder="Min"
                  disabled={propertyDisabled}
                  aria-label={`${materialPropertyConfig.label} minimum`}
                />
                <span className="material-property-range__separator">–</span>
                <input
                  type="number"
                  inputMode="decimal"
                  value={materialPropertyMax}
                  onChange={(event) => onMaterialPropertyMaxChange(event.target.value)}
                  placeholder="Max"
                  disabled={propertyDisabled}
                  aria-label={`${materialPropertyConfig.label} maximum`}
                />
                <span className="material-property-range__unit">
                  {materialPropertyConfig.unitDisplay}
                </span>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
};
